import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { readBoard, useGetBoardDetail } from '@/hooks/apiHooks';
import { ModalInner } from '@/components/Board/BoardDetailModal/styles';
import { Box, CircularProgress, Modal } from '@mui/material';
import BoardDetail from '@/components/Board/BoardDetail';
import { BoardCommentSection } from '@/types/board';
import useLayoutSize from '@/hooks/useLayoutSize';
import { LayoutType } from '@/constant/layout';
import { ActionType } from '@/constant/button';
import { useDialog } from '@/hooks/global-tools';
import { EventPageAreaType, GTMEventName } from '@/constant/gtm';
import { useGTMEventTracker } from '@/hooks/gtm/useGTMEventTracker';

export type BoardDetailModalRef = {
  open: (documentId: string, gtmValues?: Record<string, any>) => void;
  close: () => void;
};

export type BoardDetailModalProps = {
  pageArea: EventPageAreaType;
  areaName?: string;
  title?: string;
  onChanged?: (action: ActionType, documentId: string) => void;
  onChange?: (id: string, changes: Record<string, any>) => void;
  disabled?: boolean;
};

export const BoardDetailHeaderId = 'board-detail-header';
const modalHeaderId = `modal-${BoardDetailHeaderId}`;

const BoardDetailModal = forwardRef<BoardDetailModalRef, BoardDetailModalProps>(
  (props, ref) => {
    const {
      pageArea,
      areaName = '',
      title = '',
      onChanged,
      onChange,
      disabled,
    } = props;

    const [gtmValues, setGtmValues] = useState<Record<string, any> | null>(
      null,
    );
    const eventTracker = useGTMEventTracker(pageArea);
    const dialog = useDialog();

    const { isMobile, isSmallDesktop } = useLayoutSize(
      LayoutType.BOARD_DETAIL_MODAL,
    );

    const [documentId, setDocumentId] = useState<string>('');
    const [isOpen, setIsOpen] = useState(false);
    const [isRead, setIsRead] = useState(false);
    const [isRowDirection, setIsRowDirection] = useState(false);

    const { data, isLoading, mutate, error } = useGetBoardDetail(documentId);
    const isSandboxDesktop = !!data?.config.isSandbox && !isMobile;

    const modalInnerRef = useRef<HTMLDivElement>(null);
    const MobilePadding = '20px';
    const PCPadding = '24px';
    const [modalPadding, setModalPadding] = useState(
      isMobile ? MobilePadding : PCPadding,
    );

    useEffect(() => {
      if (isRead && data && data.id === documentId) {
        mutate({ ...data, watchCount: data.watchCount + 1 }, false);
      }
    }, [data?.id, isRead]);

    useEffect(() => {
      setIsRowDirection(
        !isMobile &&
          !isSmallDesktop &&
          data?.config.commentSection === BoardCommentSection.RIGHT,
      );
      setModalPadding(isMobile ? MobilePadding : PCPadding);
    }, [data, isMobile, isSmallDesktop]);

    useEffect(() => {
      if (isOpen && !isLoading) {
        if (error) {
          const e = error as any;
          dialog.show({
            message: e?.response?.data?.message ?? '에러가 발생했습니다',
            onConfirm: () => setIsOpen(false),
          });
        } else if (data) {
          // GA Social - 7번
          const { id, subject, config, categories, createdBy } = data;
          eventTracker(GTMEventName.viewPost, {
            area_name: areaName,
            post_title: subject,
            important: config.isImportant,
            post_category: categories.map((item) => item.name).join(','),
            creator: `${createdBy.name} ${createdBy.positionName}`,
            contents_id: id,
            ...gtmValues,
          });
          setGtmValues(null);
        }
      }
    }, [isOpen, isLoading]);

    const close = () => {
      setIsOpen(false);
      setDocumentId('');

      if (data && onChange) {
        // 리스트 Mutate
        onChange(data.id, { isRead: true });
      }
    };

    const sendClickPostActionEvent = (
      type: '확장해서 보기' | '축소해서 보기' | '닫기',
    ) => {
      if (!data) return;

      // GA Social - 9번
      eventTracker(GTMEventName.clickPostAction, {
        area_name: areaName || 'Social 목록',
        action_type: type,
        post_category: data.categories.map((item) => item.name).join(','),
        contents: data.subject,
        contents_id: data.id,
        state: undefined,
      });
    };

    useImperativeHandle(ref, () => ({
      open: (documentId: string, values?: Record<string, any>) => {
        if (values) setGtmValues(values);
        setDocumentId(documentId);
        setIsRead(false);
        setIsOpen(true);

        if (documentId) {
          readBoard(documentId, () => {
            setIsRead(true);
          });
        }
      },
      close,
    }));

    return (
      <Modal
        open={isOpen}
        onClose={close}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
        sx={{ '> .MuiBox-root:focus': { outline: 'none' } }}
      >
        <ModalInner
          ref={modalInnerRef}
          contentRef={modalInnerRef}
          isMobile={isMobile}
          isSandbox={isSandboxDesktop}
          maxWidth={isSandboxDesktop ? 1408 : undefined}
          viewportMargin={isSandboxDesktop ? 20 : undefined}
          resetExpandKey={
            data?.config.isSandbox ? `${documentId}:${isOpen}` : undefined
          }
          title={title}
          onClose={() => {
            close();
            sendClickPostActionEvent('닫기');
          }}
          onChangeExpand={(isExpand) =>
            sendClickPostActionEvent(
              isExpand ? '확장해서 보기' : '축소해서 보기',
            )
          }
          sx={{
            '--detail-comment-width': isRowDirection ? '420px' : null,
            ...(data?.config.isSandbox && isMobile
              ? { '.button_wrap': { display: 'none' } }
              : {}),
          }}
        >
          {isLoading || !data ? (
            <Box margin={'30vh auto'} textAlign={'center'}>
              <CircularProgress />
            </Box>
          ) : (
            <BoardDetail
              scrollRef={modalInnerRef}
              data={data}
              isRowDirection={isRowDirection}
              headerId={modalHeaderId}
              disabled={disabled}
              onChange={onChange}
              mutate={mutate}
              onDeleted={() => {
                if (onChanged) onChanged(ActionType.DELETE, documentId);
                close();
              }}
              onEdited={close}
              onClose={data.config.isSandbox && isMobile ? close : undefined}
              className={'isModal'}
              showSandboxExpandControl={data.config.isSandbox && isMobile}
              sx={{
                '&.isModal': {
                  '--board-detail-padding-X': modalPadding,
                  paddingX: modalPadding,
                  paddingBottom: '96px',
                  '&.isMobile': {
                    '& .util-button-wrap': {
                      marginRight: `${28 + 12}px`,
                    },
                  },
                  '& .util-button-wrap': {
                    marginRight: `${24 * 2 + 12 * 2}px`,
                  },
                  '&.isRowDirection': {
                    paddingBottom: '0px',
                    '.board-feedback-wrap': {
                      height: '100%',
                      minHeight: 'calc(100vh - 80px)',
                      '#board-feedback': {
                        marginTop: '64px',
                      },
                    },
                  },
                  '& .fixed-style': {
                    '#board-detail-comment-input-wrapper': {
                      width: 'calc(var(--detail-comment-width) + 25px)',
                      margin: '0 0 0 auto',
                    },
                  },
                  // Sandbox mode: full-bleed content area and bottom bar
                  '&.isSandbox': {
                    paddingBottom: 0,
                    '& .board-content-wrap': {
                      marginLeft: `-${modalPadding}`,
                      marginRight: `-${modalPadding}`,
                      width: `calc(100% + ${modalPadding} * 2)`,
                    },
                  },
                },
              }}
            />
          )}
        </ModalInner>
      </Modal>
    );
  },
);

BoardDetailModal.displayName = 'BoardDetailModal';
export default BoardDetailModal;
